/**
 * PlayersTrackerTab — Suivi consommation par joueur avec historique horodaté.
 *
 * Workflow:
 *   - Coach ajoute un joueur (nom + table optionnelle)
 *   - Choisit un jeu actif pour le joueur
 *   - À chaque partie : clique "+1 partie" (incrément immédiat au prix catalogue)
 *   - Ou bien "+1h forfait" à 12 000 F (tarif global)
 *   - L'historique horodaté s'affiche en dessous (Partie 1 à 14h05 · +6 000 F …)
 *   - À chaque clic : micro-animation "+ X F" qui pop + bip sonore
 *   - Cocher plusieurs joueurs → transmettre tous en 1 bon
 */
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Users, UserPlus, Trash2, Plus, Send, RefreshCw, Loader2, Gamepad2,
  Hash, Clock, X, History,
} from "lucide-react";
import { playBeep } from "../../lib/notificationBeep";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_HOURLY_RATE = 12000;

const PlayersTrackerTab = ({ currentUser, catalog }) => {
  const actorRole = currentUser?.role || "coach_jeux";
  const actorName = currentUser?.full_name || currentUser?.username || "Coach";

  const [players, setPlayers] = useState([]);
  const [openTables, setOpenTables] = useState([]); // Tables ouvertes en salle
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState("");
  const [newTable, setNewTable] = useState("");
  const [selected, setSelected] = useState({});
  const [activeJeu, setActiveJeu] = useState({}); // playerId -> jeu_product_id
  const [pendingIncrement, setPendingIncrement] = useState(null); // {playerId, amount, ts}
  const [busy, setBusy] = useState({}); // playerId -> bool
  const flashRef = useRef({}); // playerId -> last increment animation key

  const fetchPlayers = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/coach/players`, {
        params: { actor_role: actorRole, actor_name: actorName, status: "open" },
      });
      setPlayers(r.data.players || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  }, [actorRole, actorName]);

  // Liste des tables ouvertes (toutes serveurs confondus) pour permettre au coach
  // de rattacher un joueur à la table où il consomme déjà des plats/boissons.
  // ⚠ On affiche TOUTES les tables (y compris celles en `ready_to_invoice`) pour
  // permettre au coach de continuer à imputer un client jusqu'à la facturation finale.
  const fetchOpenTables = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/caisse/tables`);
      const tables = (r.data.tables || []).filter(
        (t) => t.status !== "invoiced"
      );
      // Tri par numéro de table ascendant
      tables.sort((a, b) => (a.table_number || 0) - (b.table_number || 0));
      setOpenTables(tables);
    } catch {
      // silencieux : si endpoint indispo, le coach peut toujours saisir le n° manuellement
    }
  }, []);

  useEffect(() => {
    fetchPlayers();
    fetchOpenTables();
    // Rafraîchit la liste des tables toutes les 15s pour suivre les ouvertures en salle
    const t = setInterval(fetchOpenTables, 15000);
    return () => clearInterval(t);
  }, [fetchPlayers, fetchOpenTables]);

  const addPlayer = async () => {
    // Nom optionnel : le backend auto-génère "Joueur N" si vide. Cas typique :
    // client venu jouer 1 partie rapide sans donner son nom.
    const name = newName.trim();
    try {
      await axios.post(`${API}/coach/players`, {
        player_name: name, coach_name: actorName, coach_role: actorRole,
        table_number: newTable ? Number(newTable) : null,
      });
      setNewName(""); setNewTable("");
      toast.success(name ? `Joueur ${name} ajouté` : "Joueur anonyme ajouté");
      fetchPlayers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const deletePlayer = async (p) => {
    if (!window.confirm(`Supprimer "${p.player_name}" et toutes ses parties ?`)) return;
    try {
      await axios.delete(`${API}/coach/players/${p.id}`, { params: { actor_role: actorRole } });
      fetchPlayers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  // Rattache / change / retire la table d'un joueur existant
  const changePlayerTable = async (p) => {
    const current = p.table_number != null ? String(p.table_number) : "";
    const tablesLabel = openTables.length
      ? openTables.map((t) => `T${t.table_number}`).join(", ")
      : "(aucune table ouverte actuellement)";
    const prompt = window.prompt(
      `Changer la table de "${p.player_name}".\n\n` +
      `Tables ouvertes : ${tablesLabel}\n\n` +
      `Saisir le n° de table (vide = retirer le rattachement) :`,
      current
    );
    if (prompt === null) return;
    const raw = prompt.trim();
    const tableNumber = raw === "" ? null : Number(raw);
    if (raw !== "" && (!Number.isFinite(tableNumber) || tableNumber < 0)) {
      return toast.error("N° de table invalide");
    }
    try {
      await axios.patch(`${API}/coach/players/${p.id}`, {
        table_number: tableNumber,
        actor_role: actorRole,
      });
      toast.success(tableNumber == null ? "Table retirée" : `Joueur rattaché à T${tableNumber}`);
      fetchPlayers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const removeConsumption = async (playerId, idx) => {
    try {
      await axios.delete(`${API}/coach/players/${playerId}/consume/${idx}`, { params: { actor_role: actorRole } });
      fetchPlayers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const flash = (playerId, amount) => {
    const key = Date.now();
    flashRef.current[playerId] = key;
    setPendingIncrement({ playerId, amount, key });
    setTimeout(() => {
      setPendingIncrement((cur) => (cur && cur.key === key ? null : cur));
    }, 1200);
    try { playBeep({ freq: 1500, duration: 0.06, volume: 0.5, count: 1, gap: 0 }); } catch {}
  };

  const addParty = async (p) => {
    const jeuId = activeJeu[p.id];
    const product = catalog.find((c) => c.id === jeuId);
    if (!product) return toast.error("Choisissez un jeu actif pour ce joueur");
    setBusy((b) => ({ ...b, [p.id]: true }));
    try {
      await axios.post(`${API}/coach/players/${p.id}/consume`, {
        jeu_product_id: product.id,
        jeu_name: product.name,
        billing_mode: "parties",
        parties: 1,
        unit_price: product.price || 0,
        actor_name: actorName, actor_role: actorRole,
      });
      flash(p.id, product.price || 0);
      fetchPlayers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy((b) => ({ ...b, [p.id]: false }));
    }
  };

  const addHour = async (p) => {
    const jeuId = activeJeu[p.id];
    const product = catalog.find((c) => c.id === jeuId);
    if (!product) return toast.error("Choisissez un jeu actif pour ce joueur");
    setBusy((b) => ({ ...b, [p.id]: true }));
    try {
      await axios.post(`${API}/coach/players/${p.id}/consume`, {
        jeu_product_id: product.id,
        jeu_name: product.name,
        billing_mode: "hourly",
        hours: 1,
        hourly_rate: DEFAULT_HOURLY_RATE,
        actor_name: actorName, actor_role: actorRole,
      });
      flash(p.id, DEFAULT_HOURLY_RATE);
      fetchPlayers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy((b) => ({ ...b, [p.id]: false }));
    }
  };

  const selectedIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);
  const selectedTotal = useMemo(
    () => players.filter((p) => selected[p.id]).reduce((s, p) => s + (Number(p.total) || 0), 0),
    [selected, players],
  );

  const transmitSelected = async () => {
    if (selectedIds.length === 0) return toast.error("Cochez au moins un joueur");
    if (!window.confirm(`Transmettre ${selectedIds.length} joueur(s) en un seul bon ?`)) return;
    try {
      const r = await axios.post(`${API}/coach/players/transmit`, {
        player_ids: selectedIds, actor_name: actorName, actor_role: actorRole,
      });
      toast.success(`Bon transmis · ${r.data.total.toLocaleString("fr-FR")} F`);
      setSelected({});
      fetchPlayers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="space-y-3" data-testid="players-tracker-tab">
      {/* Add player */}
      <Card className="bg-slate-800/60 border-purple-500/40">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <UserPlus className="w-4 h-4 text-purple-400" />
            Ajouter un joueur à suivre
            {openTables.length > 0 && (
              <Badge className="bg-blue-500/20 text-blue-200 text-[10px] ml-auto">
                {openTables.length} table{openTables.length > 1 ? "s" : ""} ouverte{openTables.length > 1 ? "s" : ""} en salle
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <Input value={newName} onChange={(e) => setNewName(e.target.value)}
                 placeholder="Nom du joueur (optionnel)"
                 className="bg-slate-900 border-slate-700 h-9 text-sm sm:col-span-2"
                 data-testid="new-player-name" />
          <Select
            value={newTable || "none"}
            onValueChange={(v) => setNewTable(v === "none" ? "" : v)}
          >
            <SelectTrigger
              className="bg-slate-900 border-slate-700 h-9 text-sm"
              data-testid="new-player-table-select"
            >
              <SelectValue placeholder="Choisir une table de la salle" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700 max-h-[280px]">
              <SelectItem value="none" className="text-amber-300 italic font-medium">
                ⚡ Pas de table — facturer le client seul
              </SelectItem>
              {openTables.map((t) => {
                const itemsCount = (t.items || []).length;
                const total = (t.items || []).reduce(
                  (s, it) => s + Number(it.price || 0) * Number(it.quantity || 0),
                  0
                );
                const isOccupied = itemsCount > 0;
                const isReady = t.status === "ready_to_invoice";
                return (
                  <SelectItem
                    key={t.id}
                    value={String(t.table_number)}
                    className="text-white text-sm"
                    data-testid={`option-table-${t.table_number}`}
                  >
                    <div className="flex items-center justify-between gap-3 w-full">
                      <span className="flex items-center gap-1.5">
                        <Hash className={`w-3 h-3 ${isOccupied ? "text-amber-400" : "text-blue-400"}`} />
                        <span className="font-semibold">T{t.table_number}</span>
                        <span className="text-slate-400 text-xs">· {t.server_name || "serveur"}</span>
                        {isReady && <Badge className="bg-amber-500/30 text-amber-200 text-[9px] px-1.5 py-0">BON</Badge>}
                      </span>
                      <span className={`text-xs ${isOccupied ? "text-emerald-300" : "text-slate-500 italic"}`}>
                        {isOccupied ? `${itemsCount} art. · ${total.toLocaleString("fr-FR")} F` : "libre"}
                      </span>
                    </div>
                  </SelectItem>
                );
              })}
              {openTables.length === 0 && (
                <div className="text-slate-400 text-xs px-2 py-3 italic text-center">
                  Aucune table ouverte en salle pour le moment.
                </div>
              )}
            </SelectContent>
          </Select>
          {/* Option pour saisir un n° libre si la table n'apparaît pas (mode dégradé) */}
          <div className="sm:col-span-3 flex items-center gap-2 text-[11px] text-slate-400">
            <span>Ou saisir un n° libre :</span>
            <Input
              type="number"
              value={newTable && !openTables.some((t) => String(t.table_number) === String(newTable)) ? newTable : ""}
              onChange={(e) => setNewTable(e.target.value)}
              placeholder="N° table"
              className="bg-slate-900 border-slate-700 h-7 text-xs w-24"
              data-testid="new-player-table-manual"
            />
            <Button
              size="sm"
              variant="ghost"
              onClick={fetchOpenTables}
              className="text-slate-300 h-7 px-2"
              title="Rafraîchir la liste des tables"
            >
              <RefreshCw className="w-3 h-3" />
            </Button>
          </div>
          <Button onClick={addPlayer}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white h-9 text-sm sm:col-span-3"
                  data-testid="add-player-btn">
            <UserPlus className="w-4 h-4 mr-1" /> Démarrer le suivi
          </Button>
        </CardContent>
      </Card>

      {/* Selection summary */}
      {selectedIds.length > 0 && (
        <Card className="bg-emerald-900/30 border-emerald-500/50 sticky top-2 z-10">
          <CardContent className="p-2.5 flex items-center justify-between gap-2 text-sm">
            <span className="text-emerald-100">
              <strong>{selectedIds.length}</strong> joueur(s) · <strong>{selectedTotal.toLocaleString("fr-FR")} F</strong>
            </span>
            <Button size="sm" onClick={transmitSelected}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white h-8"
                    data-testid="transmit-selected">
              <Send className="w-3.5 h-3.5 mr-1" /> Transmettre
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center justify-between">
        <h3 className="text-sm text-slate-300 font-semibold flex items-center gap-2">
          <Users className="w-4 h-4 text-purple-400" /> Joueurs en cours ({players.length})
        </h3>
        <Button variant="ghost" size="sm" onClick={fetchPlayers} disabled={loading}
                className="text-slate-300 h-7 text-[11px]">
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
        </Button>
      </div>

      {players.length === 0 && !loading && (
        <Card className="bg-slate-800/40 border-slate-700">
          <CardContent className="p-6 text-center text-slate-500 italic text-sm">
            Aucun joueur en cours.
          </CardContent>
        </Card>
      )}

      {players.map((p) => {
        const isSel = !!selected[p.id];
        const jeuId = activeJeu[p.id];
        const product = catalog.find((c) => c.id === jeuId);
        const isFlash = pendingIncrement && pendingIncrement.playerId === p.id;
        const isBusy = !!busy[p.id];
        // Compteur parties / forfait par jeu (résumé)
        const summary = (p.items || []).reduce((acc, it) => {
          const key = it.jeu_name + "|" + (it.billing_mode || "parties");
          if (!acc[key]) acc[key] = { jeu_name: it.jeu_name, mode: it.billing_mode || "parties", count: 0, total: 0, hours: 0 };
          acc[key].count += (it.parties || 1);
          acc[key].total += (it.total || 0);
          if (it.billing_mode === "hourly") acc[key].hours += (it.hours || 0);
          return acc;
        }, {});

        return (
          <Card key={p.id}
                className={`relative overflow-hidden ${isSel ? "bg-emerald-900/20 border-emerald-500/40" : "bg-slate-800/60 border-slate-700"}`}
                data-testid={`player-card-${p.id}`}>
            {/* Animation +X F */}
            {isFlash && (
              <div key={pendingIncrement.key}
                   className="absolute top-2 right-2 z-20 pointer-events-none text-emerald-300 font-bold text-lg"
                   style={{
                     animation: "popUp 1.1s ease-out forwards",
                   }}>
                +{pendingIncrement.amount.toLocaleString("fr-FR")} F
              </div>
            )}
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center justify-between gap-2">
                <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
                  <input type="checkbox" checked={isSel}
                         onChange={(e) => setSelected((s) => ({ ...s, [p.id]: e.target.checked }))}
                         className="w-4 h-4 accent-emerald-500"
                         data-testid={`player-select-${p.id}`} />
                  <Users className="w-4 h-4 text-purple-400 shrink-0" />
                  <span className="truncate">{p.player_name}</span>
                  {p.table_number != null ? (
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); changePlayerTable(p); }}
                      className="inline-flex"
                      title="Cliquer pour changer la table"
                      data-testid={`player-table-${p.id}`}
                    >
                      <Badge className="bg-blue-500/30 hover:bg-blue-500/50 text-blue-200 text-[10px] cursor-pointer">
                        <Hash className="w-2.5 h-2.5 mr-0.5" />T{p.table_number}
                      </Badge>
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); changePlayerTable(p); }}
                      className="inline-flex"
                      title="Rattacher à une table de la salle"
                      data-testid={`player-attach-table-${p.id}`}
                    >
                      <Badge className="bg-slate-700/60 hover:bg-blue-500/40 text-slate-300 hover:text-blue-200 text-[10px] cursor-pointer border border-dashed border-slate-500">
                        <Plus className="w-2.5 h-2.5 mr-0.5" />Table
                      </Badge>
                    </button>
                  )}
                  <Badge className="bg-emerald-700/50 text-emerald-100 text-[11px]">
                    {(p.total || 0).toLocaleString("fr-FR")} F
                  </Badge>
                </label>
                <Button size="sm" variant="ghost" onClick={() => deletePlayer(p)}
                        className="text-rose-400 hover:text-rose-300 h-7 w-7 p-0"
                        title="Supprimer">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-xs">
              {/* Résumé par jeu */}
              {Object.keys(summary).length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {Object.values(summary).map((s, i) => (
                    <div key={i} className={`flex items-center gap-1.5 rounded p-1.5 ${s.mode === "hourly" ? "bg-amber-900/30 border border-amber-500/30" : "bg-purple-900/30 border border-purple-500/30"}`}>
                      <Gamepad2 className={`w-3 h-3 ${s.mode === "hourly" ? "text-amber-400" : "text-purple-400"}`} />
                      <span className="flex-1 truncate text-slate-200 text-[11px]">{s.jeu_name}</span>
                      {s.mode === "hourly" ? (
                        <Badge className="bg-amber-700/50 text-amber-100 text-[9px]">{s.hours}h</Badge>
                      ) : (
                        <Badge className="bg-purple-700/50 text-purple-100 text-[9px]">x{s.count}</Badge>
                      )}
                      <span className="text-emerald-300 text-[10px] font-bold">{s.total.toLocaleString("fr-FR")} F</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Quick-add controls */}
              <div className="border-t border-slate-700 pt-2 space-y-1.5">
                <Select value={jeuId || ""} onValueChange={(v) => setActiveJeu((a) => ({ ...a, [p.id]: v }))}>
                  <SelectTrigger className="bg-slate-900 border-slate-700 h-8 text-[11px]"
                                 data-testid={`active-jeu-${p.id}`}>
                    <SelectValue placeholder="Choisir un jeu actif…" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    {catalog.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name} — {c.price} F</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="grid grid-cols-2 gap-1.5">
                  <Button size="sm" disabled={!product || isBusy}
                          onClick={() => addParty(p)}
                          className="bg-purple-600 hover:bg-purple-700 text-white h-9 text-xs font-semibold active:scale-95 transition"
                          data-testid={`add-party-${p.id}`}>
                    <Plus className="w-4 h-4 mr-0.5" /> +1 partie
                    {product && <span className="ml-1 text-[10px] opacity-80">({product.price} F)</span>}
                  </Button>
                  <Button size="sm" disabled={!product || isBusy}
                          onClick={() => addHour(p)}
                          className="bg-amber-600 hover:bg-amber-700 text-white h-9 text-xs font-semibold active:scale-95 transition"
                          data-testid={`add-hour-${p.id}`}>
                    <Clock className="w-4 h-4 mr-0.5" /> +1h forfait
                    <span className="ml-1 text-[10px] opacity-80">({DEFAULT_HOURLY_RATE.toLocaleString("fr-FR")} F)</span>
                  </Button>
                </div>
              </div>

              {/* Historique horodaté */}
              {(p.items || []).length > 0 && (
                <div className="border-t border-slate-700 pt-2">
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1">
                    <History className="w-3 h-3" /> Historique ({p.items.length})
                  </div>
                  <div className="space-y-0.5 max-h-[140px] overflow-y-auto pr-1">
                    {(p.items || []).slice().reverse().map((it, idx) => {
                      const realIdx = p.items.length - 1 - idx;
                      const isH = it.billing_mode === "hourly";
                      const partyNumber = p.items.slice(0, realIdx + 1).filter((x) => x.jeu_name === it.jeu_name && (x.billing_mode || "parties") === (it.billing_mode || "parties")).length;
                      return (
                        <div key={realIdx} className="flex items-center gap-2 bg-slate-900/40 rounded px-2 py-1 text-[11px]"
                             data-testid={`player-history-${p.id}-${realIdx}`}>
                          <span className="text-slate-500 text-[10px] w-12 shrink-0">
                            {it.added_at ? format(new Date(it.added_at), "HH:mm") : "—"}
                          </span>
                          <Gamepad2 className={`w-3 h-3 ${isH ? "text-amber-400" : "text-purple-400"} shrink-0`} />
                          <span className="flex-1 min-w-0 truncate text-slate-200">
                            {isH ? `Forfait ${it.hours}h` : `Partie #${partyNumber}`}
                            {" · "}
                            <span className="text-slate-400">{it.jeu_name}</span>
                          </span>
                          <span className="text-emerald-300 font-semibold">+{(it.total || 0).toLocaleString("fr-FR")} F</span>
                          <Button size="sm" variant="ghost" onClick={() => removeConsumption(p.id, realIdx)}
                                  className="text-rose-400 hover:text-rose-300 h-5 w-5 p-0">
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      <style>{`
        @keyframes popUp {
          0%   { opacity: 0; transform: translateY(20px) scale(0.7); }
          15%  { opacity: 1; transform: translateY(0) scale(1.2); }
          40%  { opacity: 1; transform: translateY(-10px) scale(1); }
          100% { opacity: 0; transform: translateY(-50px) scale(0.9); }
        }
      `}</style>
    </div>
  );
};

export default PlayersTrackerTab;
